import React, { useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/redux/hooks';
import { selectTimelineSegments } from '@/redux/selectors/packagingTimelineSelectors';
import { selectPackagingIsEditingTimeline, selectPackagingTimelineScale } from '@/redux/selectors/packagingSelectors';
import { SegmentItem } from '@/redux/slices/packagingSlice.types';
import { SEGMENTS_GAP } from '@/components/Packaging/constants';
import GroupMarker from '@/components/Packaging/LayeredTimeline/common/GroupMarker';

type Group = { left: number; right: number; item: SegmentItem }[];

const CloseSegmentGroupsMarkers: React.FC = () => {
  const scale = useAppSelector(selectPackagingTimelineScale);
  const hideGroups = useAppSelector(selectPackagingIsEditingTimeline);
  const segments = useAppSelector(selectTimelineSegments);
  const ref = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [groups, setGroups] = useState<Group[]>([]);

  useLayoutEffect(() => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (!ref.current?.parentNode) return;

      const elGroups: Set<HTMLElement>[] = []; // This array is needed just to make sure we don't add an element to the group twice
      const groups: Group[] = [];
      let curGroupIndex = 0;
      const segmentEls = ref.current.parentNode.querySelectorAll<HTMLElement>('[data-segment-id]');
      let offset = 0;

      for (let i = 0; i < segmentEls.length; i++) {
        const cur = segmentEls[i];
        const curBCRect = cur.getBoundingClientRect();
        if (i === 0) offset = curBCRect.left;
        const curLeft = curBCRect.left - offset;
        const curWidth = curBCRect.width;
        const next = segmentEls[i + 1];

        if (!next) continue;

        const nextBCRect = next.getBoundingClientRect();
        const nextLeft = nextBCRect.left - offset;
        const nextWidth = nextBCRect.width;

        if (curWidth < SEGMENTS_GAP + 4) {
          if (!groups[curGroupIndex]) {
            elGroups[curGroupIndex] = new Set();
            groups[curGroupIndex] = [];
          }
          if (!elGroups[curGroupIndex].has(cur)) {
            elGroups[curGroupIndex].add(cur);
            groups[curGroupIndex].push({ left: curLeft, right: curLeft + curWidth, item: getItemByElement(cur, segments) });
          }

          if (nextWidth < SEGMENTS_GAP + 4) {
            elGroups[curGroupIndex].add(next);
            groups[curGroupIndex].push({ left: nextLeft, right: nextLeft + nextWidth, item: getItemByElement(next, segments) });
          }
        } else {
          curGroupIndex++;
        }
      }

      setGroups(groups);
    }, 100);

    return () => {
      setGroups([]);
    };
  }, [segments, scale]);

  return (
    <div ref={ref}>
      {groups.map((group, index) => {
        const firstItem = group.at(0);
        const lastItem = group.at(-1);

        if (!firstItem || !lastItem) return;

        const start = firstItem.left;
        const end = lastItem.right;
        const segments = group.map((g) => g.item);

        return <GroupMarker key={index} show={!hideGroups} position={start + 4} width={end - start} items={segments} horizontalLine />;
      })}
    </div>
  );
};

const getItemByElement = (segmentElement: HTMLElement, layer: SegmentItem[]) => {
  const id = segmentElement.getAttribute('data-segment-id');

  if (!id) {
    throw new Error('segmentElement should have data-segment-id attribute with segment id');
  }

  const segment = layer.find((o) => o.draggableId === id);

  if (!segment) {
    throw new Error(`Can not find segment with "${id}" id`);
  }

  return segment;
};

export default CloseSegmentGroupsMarkers;
